import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Paper, Select, TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import AccountingDataTable, { DataTableColumn } from '../accounting/shared/AccountingDataTable';
import accessControlService, { AccessCard, AccessProfile } from '../../services/accessControlService';

type Severity = 'success' | 'error' | 'info' | 'warning';

interface Props {
  profiles: AccessProfile[];
  /** Atama/çıkarma sonrası profil listesindeki kart sayaçları tazelenir. */
  reloadProfiles: () => Promise<void>;
  notify: (message: string, severity?: Severity) => void;
  cm: (key: string, defaultValue: string, options?: Record<string, unknown>) => string;
}

const CardPermissionsTab: React.FC<Props> = ({ profiles, reloadProfiles, notify, cm }) => {
  const navigate = useNavigate();

  const [cards, setCards] = useState<AccessCard[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filterProfileId, setFilterProfileId] = useState<number | ''>('');
  const [unassignedOnly, setUnassignedOnly] = useState(false);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [dialog, setDialog] = useState<'assign' | 'remove' | null>(null);
  const [dialogProfileId, setDialogProfileId] = useState<number | ''>('');
  const [processing, setProcessing] = useState(false);

  // Arama 400 ms debounce ile sunucuya gider (kart sayısı binlerce olabilir).
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadCards = useCallback(async () => {
    setLoading(true);
    try {
      const res = await accessControlService.getCards({
        page,
        pageSize,
        search: search || undefined,
        profileId: filterProfileId === '' ? undefined : filterProfileId,
        unassignedOnly: unassignedOnly || undefined,
      });
      setCards(res.data?.data || []);
      setTotalCount(res.data?.totalCount || 0);
    } catch (error: any) {
      setCards([]);
      setTotalCount(0);
      notify(error?.response?.data?.message || cm('accessManagement.errors.cardsLoadFailed', 'Cards could not be loaded.'), 'error');
    }
    setLoading(false);
  }, [page, pageSize, search, filterProfileId, unassignedOnly, notify, cm]);

  useEffect(() => { loadCards(); }, [loadCards]);

  const toggleId = (id: string) => {
    const numeric = Number(id);
    setSelectedIds((prev) => (prev.includes(numeric) ? prev.filter((x) => x !== numeric) : [...prev, numeric]));
  };

  const toggleAll = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSelectedIds(event.target.checked ? cards.map((c) => c.id) : []);

  const openDialog = (mode: 'assign' | 'remove') => {
    // Çıkarma kipinde ön seçim, seçili kartlarda gerçekten bulunan bir profil olmalı;
    // aksi halde kullanıcı hiç etkisi olmayan bir işlem çalıştırır.
    const fallback = mode === 'remove'
      ? cards.find((c) => selectedIds.includes(c.id) && c.profiles.length > 0)?.profiles[0]?.id
      : (filterProfileId === '' ? profiles[0]?.id : filterProfileId);

    setDialogProfileId(fallback ?? '');
    setDialog(mode);
  };

  const runDialog = async () => {
    if (dialogProfileId === '' || selectedIds.length === 0) return;

    setProcessing(true);
    try {
      if (dialog === 'assign') {
        const res = await accessControlService.assignCardProfiles(selectedIds, dialogProfileId);
        notify(cm('accessManagement.cardPermissions.assigned', 'Group assigned to {{n}} cards.', { n: res.data?.assigned ?? 0 }), 'success');
      } else {
        const res = await accessControlService.removeCardProfiles(selectedIds, dialogProfileId);
        notify(cm('accessManagement.cardPermissions.removed', 'Group removed from {{n}} cards.', { n: res.data?.removed ?? 0 }), 'success');
      }
      setDialog(null);
      setSelectedIds([]);
      await loadCards();
      await reloadProfiles();
    } catch (error: any) {
      const fallbackKey = dialog === 'assign' ? 'accessManagement.errors.assignFailed' : 'accessManagement.errors.removeFailed';
      notify(error?.response?.data?.message || cm(fallbackKey, 'Operation failed.'), 'error');
    }
    setProcessing(false);
  };

  const columns: DataTableColumn<AccessCard>[] = [
    {
      id: 'cardNumber',
      label: cm('accessManagement.cardPermissions.cardNumber', 'Card No'),
      width: '15%',
      render: (row) => <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{row.cardNumber}</Typography>,
    },
    {
      id: 'accountName',
      label: cm('accessManagement.cardPermissions.owner', 'Card Owner'),
      width: '22%',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant="body2">{row.accountName || '—'}</Typography>
          <Chip
            label={row.ownerType === 'PERSONNEL'
              ? cm('accessManagement.cardPermissions.ownerPersonnel', 'Personnel')
              : cm('accessManagement.cardPermissions.ownerAccount', 'Account')}
            size="small"
            variant="outlined"
            color={row.ownerType === 'PERSONNEL' ? 'secondary' : 'default'}
            sx={{ height: 18, fontSize: '0.65rem' }}
          />
        </Box>
      ),
    },
    {
      id: 'cardUID',
      label: cm('accessManagement.cardPermissions.cardUID', 'Card UID'),
      width: '15%',
      render: (row) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
          {row.cardUID || '—'}
        </Typography>
      ),
    },
    {
      id: 'turnstileUID',
      label: cm('accessManagement.cardPermissions.turnstileUID', 'Turnstile UID'),
      width: '10%',
      align: 'center',
      render: (row) => (row.hasTurnstileUID
        ? <Chip size="small" color="success" variant="outlined" label="✓" />
        : <Typography variant="caption" color="text.disabled">—</Typography>),
    },
    {
      id: 'profiles',
      label: cm('accessManagement.cardPermissions.assignedProfiles', 'Groups'),
      width: '28%',
      render: (row) => (row.profiles.length === 0 ? (
        <Typography variant="caption" color="text.disabled">
          {cm('accessManagement.cardPermissions.noProfile', 'No group')}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {row.profiles.map((profile) => (
            <Chip
              key={profile.id}
              size="small"
              label={profile.name}
              sx={{
                bgcolor: profile.color || 'grey.300',
                color: profile.color ? '#fff' : 'text.primary',
                height: 22,
              }}
            />
          ))}
        </Box>
      )),
    },
    {
      id: 'status',
      label: cm('accessManagement.cardPermissions.status', 'Status'),
      width: '10%',
      align: 'center',
      render: (row) => (row.isActive
        ? <Chip size="small" color="success" variant="outlined" label={cm('accessManagement.active', 'Active')} />
        : <Chip size="small" variant="outlined" label={cm('accessManagement.inactive', 'Passive')} />),
    },
  ];

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {cm('accessManagement.cardPermissions.title', 'Card Permissions')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {cm('accessManagement.cardPermissions.hint', 'Cards are not created here.')}
            </Typography>
          </Box>
          <Button size="small" endIcon={<OpenInNewIcon />} onClick={() => navigate('/kart-yonetimi/kartlar')}>
            {cm('accessManagement.cardPermissions.goToCards', 'Card Definitions')}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            sx={{ minWidth: 260 }}
            placeholder={cm('accessManagement.cardPermissions.search', 'Search card no, account or UID')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{cm('accessManagement.cardPermissions.filterProfile', 'By group')}</InputLabel>
            <Select
              value={filterProfileId}
              label={cm('accessManagement.cardPermissions.filterProfile', 'By group')}
              onChange={(e) => {
                setFilterProfileId(e.target.value === '' ? '' : Number(e.target.value));
                setPage(1);
              }}
            >
              <MenuItem value="">
                <em>{cm('accessManagement.cardPermissions.allProfiles', 'All')}</em>
              </MenuItem>
              {profiles.map((profile) => (
                <MenuItem key={profile.id} value={profile.id}>{profile.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={unassignedOnly}
                onChange={(e) => { setUnassignedOnly(e.target.checked); setPage(1); }}
              />
            )}
            label={cm('accessManagement.cardPermissions.unassignedOnly', 'Without group')}
          />
        </Box>

        {profiles.length === 0 && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            {cm('accessManagement.cardPermissions.noProfiles', 'Create a group from the Access Groups tab first.')}
          </Alert>
        )}

        {selectedIds.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1.5 }}>
            <Typography variant="body2" fontWeight="bold">
              {cm('accessManagement.cardPermissions.selected', '{{n}} cards selected', { n: selectedIds.length })}
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<PlaylistAddCheckIcon />}
              disabled={profiles.length === 0}
              onClick={() => openDialog('assign')}
            >
              {cm('accessManagement.cardPermissions.assign', 'Assign Group')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<PlaylistRemoveIcon />}
              disabled={profiles.length === 0}
              onClick={() => openDialog('remove')}
            >
              {cm('accessManagement.cardPermissions.remove', 'Remove Group')}
            </Button>
          </Box>
        )}
      </Paper>

      <AccountingDataTable<AccessCard>
        columns={columns}
        rows={cards}
        getRowId={(row) => String(row.id)}
        loading={loading}
        serverSidePagination
        totalCount={totalCount}
        page={page - 1}
        pageSize={pageSize}
        onPageChange={(nextPage, nextPageSize) => {
          setPage(nextPage + 1);
          setPageSize(nextPageSize);
        }}
        selectable
        selectedIds={selectedIds.map(String)}
        onSelectId={toggleId}
        onSelectAll={toggleAll}
        emptyMessage={cm('accessManagement.cardPermissions.empty', 'No card found.')}
        emptyIcon={<CreditCardIcon sx={{ fontSize: 42, color: 'text.disabled' }} />}
      />

      {/* Toplu atama / çıkarma */}
      <Dialog open={!!dialog} onClose={() => !processing && setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {dialog === 'assign'
            ? cm('accessManagement.cardPermissions.assignTitle', 'Assign group to selected cards')
            : cm('accessManagement.cardPermissions.removeTitle', 'Remove group from selected cards')}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {cm('accessManagement.cardPermissions.selected', '{{n}} cards selected', { n: selectedIds.length })}
          </Typography>
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel>{cm('accessManagement.cardPermissions.chooseProfile', 'Group')}</InputLabel>
            <Select
              value={dialogProfileId}
              label={cm('accessManagement.cardPermissions.chooseProfile', 'Group')}
              onChange={(e) => setDialogProfileId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              {profiles.map((profile) => (
                <MenuItem key={profile.id} value={profile.id}>{profile.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={processing}>
            {cm('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color={dialog === 'remove' ? 'error' : 'primary'}
            onClick={runDialog}
            disabled={processing || dialogProfileId === ''}
            startIcon={processing ? <CircularProgress size={18} /> : undefined}
          >
            {dialog === 'assign'
              ? cm('accessManagement.cardPermissions.assign', 'Assign Group')
              : cm('accessManagement.cardPermissions.remove', 'Remove Group')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CardPermissionsTab;
